import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employee as EmployeeActions, updateEmployeeRequest } from 'store/Employee';
import { IStore } from 'store/types';
import { EmployeeStatus, EmployeeType, IEmployee } from 'types';

interface IGetOptionsProps {
  employee?: IEmployee;
  employeeType: EmployeeType;
  site?: { label: string; value: string };
  onClose: () => void;
  goToUser(dupUser: IEmployee): void;
  addUserToSite(empId: string): void;
  activateUser(empId: string, siteId?: string): void;
}

const getOptions = ({ employee, employeeType, site, onClose, goToUser, addUserToSite, activateUser }: IGetOptionsProps) => {
  let message;
  let rightActionLabel = '';
  let onSubmit = onClose;
  const hideLeftAction = false;

  if (employee) {
    if (employee.type === EmployeeType.ADMIN || employeeType === EmployeeType.ADMIN) {
      message = (
        <>
          <b>{employee.inviteRecipient}</b> {' is already being used by another user '} <b>{employee.name}</b>. Click below to view the user.
        </>
      );
      rightActionLabel = 'View';
      onSubmit = () => goToUser(employee);
    } else if (site) {
      if (employee.status === 'dismissed') {
        message = (
          <>
            <b>{employee.inviteRecipient}</b> {' is already being used by the dismissed user '} <b>{employee.name}</b>. Would you like to activate this user at <b>{site.label}</b>?
          </>
        );
        rightActionLabel = 'Reactivate';
        onSubmit = () => activateUser(employee.id);
      } else {
        const selectedSite = employee.sites.find((s) => s.id === site.value);

        if (selectedSite) {
          if (selectedSite.status !== 'dismissed') {
            message = (
              <>
                <b>{employee.inviteRecipient}</b> {' is already being used by another user '} <b>{employee.name}</b>. Click below to view the user.
              </>
            );
            rightActionLabel = 'View';
            onSubmit = () => goToUser(employee);
          } else {
            message = (
              <>
                <b>{employee.inviteRecipient}</b> {' is already being used by the dismissed user '} <b>{employee.name}</b>. Would you like to activate this user at <b>{site.label}</b>?
              </>
            );
            rightActionLabel = 'Reactivate';
            onSubmit = () => activateUser(employee.id, site.value);
          }
        } else {
          message = (
            <>
              <b>{employee.inviteRecipient}</b> {' is already being used by another user '} <b>{employee.name}</b> on another site. Would you like to add this user to <b>{site.label}</b>?
            </>
          );
          rightActionLabel = 'Add';
          onSubmit = () => addUserToSite(employee.id);
        }
      }
    }
  }

  return {
    message,
    rightActionLabel,
    onSubmit,
    hideLeftAction,
  };
};

interface IDuplicateUserAlertProps {
  employee?: IEmployee;
  employeeType: EmployeeType;
  addUserToSite(empId: string): void;
  activateUserSite(empId: string, siteId: string): void;
  site?: { label: string; value: string };
  onClose(): void;
  goToUser(dupUser: IEmployee): void;
  isActive?: boolean;
}

const DuplicateUserAlert = ({ employee, employeeType, addUserToSite, activateUserSite, site, onClose, goToUser, isActive }: IDuplicateUserAlertProps) => {
  const dispatch = useDispatch();
  const isAddEmployeeToSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeeActions.ADD_EMPLOYEE_TO_SITE_REQUEST));
  const isUpdateExistEmployeeToSiteLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeeActions.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
  const isActivateUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeeActions.UPDATE_EMPLOYEE_REQUEST));

  const activateUser = (empId: string, siteId?: string) => {
    if (siteId) {
      activateUserSite(empId, siteId);
    } else {
      dispatch(updateEmployeeRequest(empId, { status: EmployeeStatus.APPROVED }, { onSuccess: () => activateUserSite(empId, site.value) }));
    }
  };

  const { message, rightActionLabel, onSubmit, hideLeftAction } = getOptions({ employee, employeeType, site, onClose, goToUser, addUserToSite, activateUser });

  return (
    <Alert
      title="User already exists"
      message={message}
      onLeftAction={onClose}
      onRightAction={onSubmit}
      leftActionLabel="Cancel"
      hideLeftAction={hideLeftAction}
      rightActionLabel={rightActionLabel}
      isActive={isActive}
      isLoading={isAddEmployeeToSiteLoading || isUpdateExistEmployeeToSiteLoading || isActivateUserLoading}
    />
  );
};

export default DuplicateUserAlert;
